import type { ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { monthOf, shiftMonth } from "@/api/budgets";
import { useMonthlyReport, type Breakdown } from "@/api/reports";
import { useHousehold } from "@/app/useHousehold";
import { Amount } from "@/components/Amount";
import { ShareDonut, type Slice } from "@/components/ShareDonut";
import { CategoryBars } from "@/components/CategoryBars";
import {
  ErrorNote,
  Loading,
  PageTitle,
  Section,
  rowsClass,
  secondaryButtonClass,
} from "@/components/ui";
import { useFormat } from "@/i18n/format";
import { useT } from "@/i18n/LocaleProvider";

/**
 * A month, whole: what came in, what went out, and where it went.
 *
 * **This is not the dashboard with a date picker on it**, and the difference is
 * worth stating because the two screens look alike. The dashboard answers
 * `project.md` §5.1's one question — *am I okay this month?* — about the month
 * running now, and counts it only as far as it has actually happened, because
 * comparing eleven days against a finished month would alarm somebody about the
 * calendar. This answers *where did March go?*, about a month that has ended,
 * and so reports it whole.
 *
 * Both read the same figures from the same summation (`model.md` §4.2). Two
 * queries claiming to answer "what did Lebensmittel cost" would eventually
 * disagree, and the report disagreeing with the dashboard is exactly what costs
 * a finance product its credibility.
 *
 * **The month lives in the URL**, not in component state. A report is a thing
 * people send each other and come back to, so it needs a link and a working back
 * button — the dashboard's `?as_of=` precedent rather than the budget screen's
 * `useState`.
 */
export function ReportPage() {
  const t = useT();
  const format = useFormat();
  const { householdId = "" } = useParams();
  const household = useHousehold();
  const currency = household?.base_currency_code ?? "EUR";

  const [params, setParams] = useSearchParams();
  const thisMonth = monthOf(new Date());
  const period = params.get("period") ?? thisMonth;

  const report = useMonthlyReport(householdId, period);
  const data = report.data;

  // Forward navigation stops at the month running now. There is nothing to
  // report about a month that has not happened, and a screen that let somebody
  // walk into 2099 to find six empty months has wasted their time.
  const atLatest = period >= thisMonth;
  const goto = (next: string) => setParams({ period: next }, { replace: true });

  return (
    <div>
      <PageTitle lead={t("report.lead")}>{t("nav.report")}</PageTitle>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => goto(shiftMonth(period, -1))}
          aria-label={t("budgets.previousMonth")}
          className={`${secondaryButtonClass} min-h-11 px-3`}
        >
          ←
        </button>
        <span className="min-w-40 text-center text-sm font-medium text-ink" data-testid="report-period">
          {format.month(period)}
        </span>
        <button
          type="button"
          onClick={() => goto(shiftMonth(period, 1))}
          aria-label={t("budgets.nextMonth")}
          disabled={atLatest}
          className={`${secondaryButtonClass} min-h-11 px-3 disabled:opacity-40`}
        >
          →
        </button>
      </div>

      {report.isLoading && <Loading />}
      <ErrorNote error={report.error} />

      {data && (
        <>
          {/* The month still running is a part, and says so rather than
              presenting itself as a whole. */}
          {!data.cashflow.is_complete && (
            <p className="mt-4 text-xs text-muted" data-testid="report-partial">
              {t("report.stillRunning", { through: format.date(data.cashflow.through) })}
            </p>
          )}

          {/* Money in, money out, and what is left — a small ledger rather than
              a chart, because three numbers are not a chart (the dataviz
              skill's first question is whether it should be one at all). The
              net line carries the bookkeeper's marks: a rule above it because
              it is a sum of the two rows above, and the double rule beneath it
              because it really is the month's final total. */}
          <dl className="mt-6 max-w-sm">
            <LedgerRow label={t("report.moneyIn")} testId="report-in">
              <Amount minor={data.cashflow.in_minor} currency={currency} />
            </LedgerRow>
            <LedgerRow label={t("report.moneyOut")} testId="report-out">
              <Amount minor={data.cashflow.out_minor} currency={currency} />
            </LedgerRow>
            <LedgerRow label={t("report.net")} testId="report-net" isTotal>
              <Amount minor={data.cashflow.net_minor} currency={currency} />
            </LedgerRow>
          </dl>

          {data.is_meaningful && (
            <p className="mt-2 text-xs text-muted" data-testid="report-comparison">
              {t(
                data.direction > 0
                  ? "report.betterThanBefore"
                  : data.direction < 0
                    ? "report.worseThanBefore"
                    : "report.sameAsBefore",
                {
                  delta: format.amount(Math.abs(data.delta_minor), currency),
                  month: format.month(data.previous.period_start),
                },
              )}
            </p>
          )}

          <Section title={t("report.whereItWent")}>
            {/* The ring answers "how much of the month"; the bars beside it
                answer "compared to what" — the same money, two questions
                (ADR 0006). */}
            {/* Stacked, not side by side: the ring's legend carries names,
                shares and amounts, and beside the bars it ran into them. */}
            <div className="grid gap-10">
              <ShareDonut
                slices={slicesOf(data.spending, t("report.uncategorised"))}
                currency={currency}
                totalMinor={data.spending.total_minor}
                totalLabel={t("report.spentInTotal")}
                emptyLabel={t("report.nothingSpent")}
              />

              {data.spending.categories.length > 0 && (
                <CategoryBars
                  categories={data.spending.categories.map((c) => ({
                    category_id: c.category_id,
                    name: c.name,
                    spent_minor: c.amount_minor,
                    lines: c.lines,
                  }))}
                  currency={currency}
                />
              )}
            </div>
          </Section>

          {/* The payments behind the categories.

              The ring says a third of the month went on Wohnen; this says the
              rent was €780 of it on the 3rd. A household that wants to know why
              a category is large looks here, and neither list answers the
              other's question — which is why both are on the screen. */}
          {data.outgoings.length > 0 && (
            <Section title={t("report.largestPayments")}>
              <p className="-mt-2 mb-3 text-xs text-muted">
                {t("report.largestPaymentsLead", { n: String(data.outgoings.length) })}
              </p>
              <ul className={rowsClass}>
                {data.outgoings.map((payment) => (
                  <li
                    key={payment.transaction_id}
                    className="flex items-baseline gap-3 py-2.5 text-sm"
                    data-testid="outgoing-row"
                  >
                    <span className="w-20 shrink-0 text-xs text-muted">
                      {format.date(payment.booked_date)}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-ink">
                      {payment.payee || (
                        <span className="text-muted">{t("report.noPayee")}</span>
                      )}
                    </span>
                    {/* A divided receipt says how many parts it has rather than
                        borrowing one of their names. */}
                    <span className="hidden shrink-0 text-xs text-muted sm:inline">
                      {payment.category_name ||
                        (payment.splits > 1
                          ? t("report.dividedInto", { n: String(payment.splits) })
                          : "")}
                    </span>
                    <Amount
                      minor={payment.amount_minor}
                      currency={currency}
                      testId="outgoing-amount"
                    />
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Income folded away by default. Most households have one salary, and
              a ring of one slice is a worse way of writing a number than the
              number. A freelancer with nine invoices opens it. */}
          <details className="mt-10 group" data-testid="report-income">
            <summary className="cursor-pointer text-sm font-medium text-ink marker:text-muted">
              {t("report.whereItCameFrom", {
                total: format.amount(data.income.total_minor, currency),
              })}
            </summary>
            <div className="mt-4">
              <ShareDonut
                slices={slicesOf(data.income, t("report.uncategorised"))}
                currency={currency}
                totalMinor={data.income.total_minor}
                totalLabel={t("report.receivedInTotal")}
                emptyLabel={t("report.nothingReceived")}
              />
            </div>
          </details>
        </>
      )}
    </div>
  );
}

/**
 * One row of the small ledger above: a label and a right-aligned figure.
 *
 * The net row alone carries `.rule-sum`/`.rule-total` — the bookkeeper's marks
 * for "this follows from a sum" and "this is the final total" — because it is
 * the only row here where both are literally true.
 */
function LedgerRow({
  label,
  testId,
  isTotal = false,
  children,
}: {
  label: string;
  testId: string;
  isTotal?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={`flex items-baseline justify-between gap-4 py-1.5 text-sm ${
        isTotal ? "rule-sum rule-total mt-1 pt-2.5" : ""
      }`}
    >
      <dt className={isTotal ? "font-medium text-ink" : "text-muted"}>{label}</dt>
      <dd className={isTotal ? "font-medium" : ""} data-testid={testId}>
        {children}
      </dd>
    </div>
  );
}

/**
 * Turns a breakdown into the ring's slices.
 *
 * The uncategorised part is a slice like any other, because it is money that was
 * really spent: a ring drawn from the named categories alone would be a ring
 * quietly missing a share of the total it prints in its own middle.
 */
function slicesOf(breakdown: Breakdown, uncategorisedName: string): Slice[] {
  const slices: Slice[] = breakdown.categories.map((c) => ({
    id: c.category_id,
    name: c.name,
    amountMinor: c.amount_minor,
    shareBp: c.share_bp,
  }));

  if (breakdown.uncategorised_minor > 0) {
    slices.push({
      id: "__uncategorised__",
      name: uncategorisedName,
      amountMinor: breakdown.uncategorised_minor,
      shareBp: breakdown.uncategorised_share_bp,
    });
  }
  return slices;
}
